/**
 * Coalesced catalog loading (plan §5.4). Realtime events, restaurant
 * changes and reconnects all request a refresh here; requests arriving while
 * a run is in flight merge into exactly one trailing run, so a burst of
 * events costs at most two loads.
 */

/** A realtime `catalogUpdate` payload; no ids = everything. */
export interface ICatalogSyncPayload {
  itemIds?: number[];
  categoryIds?: number[];
}

export interface ICatalogSyncDeps {
  loadAll(): Promise<void>;
  loadCategories(): Promise<void>;
  refreshItems(ids: number[]): Promise<void>;
  /** The legacy POS modals read their own cache: kept fresh on every event. */
  reloadLegacyCache(): Promise<void> | void;
  reloadPrintStations(): Promise<void> | void;
}

export interface ICatalogSync {
  /** Resolves when a run that covers this payload finishes. */
  request(payload: ICatalogSyncPayload): Promise<void>;
  /** Resolves when nothing is running or queued. */
  whenIdle(): Promise<void>;
}

/** Above this many products a partial refresh costs more than a full load. */
export const MAX_REFRESH_IDS = 200;

interface IPendingRun {
  full: boolean;
  itemIds: Set<number>;
  categoryIds: Set<number>;
  waiters: Array<{ resolve: () => void; reject: (error: unknown) => void }>;
}

function isFull(payload: ICatalogSyncPayload): boolean {
  return !payload.itemIds && !payload.categoryIds;
}

export function createCatalogSync(deps: ICatalogSyncDeps): ICatalogSync {
  let running: Promise<void> | null = null;
  let pending: IPendingRun | null = null;

  /** A cache kept fresh beside the catalog: its failure never fails the run. */
  async function refreshAside(
    reload: () => Promise<void> | void,
    what: string
  ): Promise<void> {
    try {
      await reload();
    } catch (error) {
      console.error(`Reloading ${what} failed`, error);
    }
  }

  async function execute(run: IPendingRun): Promise<void> {
    try {
      if (run.full || run.itemIds.size > MAX_REFRESH_IDS) await deps.loadAll();
      else {
        if (run.categoryIds.size) await deps.loadCategories();
        if (run.itemIds.size) await deps.refreshItems([...run.itemIds]);
      }
    } finally {
      // Also after a failed load: the legacy POS must not keep another
      // store's products after a store switch.
      await refreshAside(deps.reloadLegacyCache, 'the legacy catalog cache');
      await refreshAside(deps.reloadPrintStations, 'the print stations');
    }
  }

  function startNext(): void {
    const run = pending;
    pending = null;
    if (!run) {
      running = null;
      return;
    }
    running = execute(run).then(
      () => {
        for (const waiter of run.waiters) waiter.resolve();
      },
      (error) => {
        for (const waiter of run.waiters) waiter.reject(error);
      }
    );
    void running.then(startNext);
  }

  function request(payload: ICatalogSyncPayload): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!pending)
        pending = {
          full: false,
          itemIds: new Set(),
          categoryIds: new Set(),
          waiters: [],
        };
      if (isFull(payload)) pending.full = true;
      for (const id of payload.itemIds ?? []) pending.itemIds.add(id);
      for (const id of payload.categoryIds ?? []) pending.categoryIds.add(id);
      pending.waiters.push({ resolve, reject });
      if (!running) startNext();
    });
  }

  async function whenIdle(): Promise<void> {
    while (running) await running;
  }

  return { request, whenIdle };
}
